import type {
  MembershipBillingOption,
  MembershipPlan,
} from '@/components/organisms/pricebook/support/membership-plan-shared';
import {
  averageRecurringOccurrencesPerYear,
  scheduleSummary,
} from '@/components/organisms/pricebook/recurring-service-editor';
import type { PlanRecurringService } from '@/hooks/membership-plans/usePlanRecurringServices';
import {
  membershipBillingPeriodLabel,
  priceMembershipOptionForEquipment,
  type MembershipSaleEquipment,
} from '@/lib/membership/home-sale-pricing';
import { EXTRA_UNIT_PRICE_UNIT_LABELS, extraUnitPriceOf } from '@/lib/membership/extra-unit-price';

type EquipmentState = 'AWAITING_LOCATION' | 'LOADING' | 'READY' | 'ERROR';

interface MembershipSalePromiseSummaryProps {
  plan: MembershipPlan;
  billingOption?: MembershipBillingOption;
  services: PlanRecurringService[];
  servicesLoading?: boolean;
  servicesError?: boolean;
  equipment: MembershipSaleEquipment[];
  equipmentState: EquipmentState;
  allowLegacyUnitVisitsWithoutEquipment?: boolean;
  historicalVisits?: boolean;
}

export function plannedMembershipVisits({
  plan,
  billingOption,
  services,
  equipment,
  equipmentState,
  allowLegacyUnitVisitsWithoutEquipment = false,
}: MembershipSalePromiseSummaryProps): number {
  const coverageRules = plan.coverageRules ?? [];
  const equipmentByCoverage = new Map(
    coverageRules.map((coverage) => [
      coverage.id,
      equipment.filter(
        (unit) =>
          unit.category.trim().toLowerCase() === coverage.equipmentCategory.trim().toLowerCase(),
      ).length,
    ]),
  );

  return services.reduce((total, service) => {
    const scheduled = averageRecurringOccurrencesPerYear(service, billingOption?.termMonths ?? 0);
    if (service.attachTo !== 'UNIT' || equipmentState !== 'READY') {
      return total + scheduled;
    }
    const coveredUnits = equipmentByCoverage.get(service.coverageId ?? '') ?? 0;
    if (coveredUnits === 0) {
      return allowLegacyUnitVisitsWithoutEquipment ? total + scheduled : total;
    }
    return total + (service.scaling === 'MULTIPLY' ? scheduled * coveredUnits : scheduled);
  }, 0);
}

export function membershipPromiseIncludesVisit({
  plan,
  services,
  equipment,
  equipmentState,
  allowLegacyUnitVisitsWithoutEquipment = false,
}: MembershipSalePromiseSummaryProps): boolean {
  const equipmentByCoverage = new Map(
    (plan.coverageRules ?? []).map((coverage) => [
      coverage.id,
      equipment.filter(
        (unit) =>
          unit.category.trim().toLowerCase() === coverage.equipmentCategory.trim().toLowerCase(),
      ).length,
    ]),
  );

  return services.some((service) => {
    if (service.durationType === 'FIXED') {
      const totalVisits = Number(service.totalVisits);
      if (Number.isFinite(totalVisits) && totalVisits <= 0) return false;
    }
    if (service.recurrenceType === 'SEASONAL' && service.recurrenceMonths.length === 0) {
      return false;
    }
    if (service.attachTo !== 'UNIT' || equipmentState !== 'READY') return true;
    const coveredUnits = equipmentByCoverage.get(service.coverageId ?? '') ?? 0;
    return coveredUnits > 0 || allowLegacyUnitVisitsWithoutEquipment;
  });
}

const ITEM_TYPE_LABELS: Record<string, string> = {
  SERVICE: 'services',
  MATERIAL: 'materials',
  EQUIPMENT: 'equipment',
  FEE: 'fees',
};

function formatEquipmentCategory(value: string): string {
  return value
    .replaceAll('_', ' ')
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
    .replace(/\bAc\b/g, 'AC')
    .replace(/\bHvac\b/g, 'HVAC');
}

function formatList(values: string[]): string {
  if (values.length <= 1) return values[0] ?? '';
  if (values.length === 2) return `${values[0]} and ${values[1]}`;
  return `${values.slice(0, -1).join(', ')}, and ${values.at(-1)}`;
}

function occurrenceLabel(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  return `${rounded} planned visit${rounded === 1 ? '' : 's'} / year`;
}

function serviceDelivery(
  service: PlanRecurringService,
  coverageNames: Map<string, string>,
): string {
  if (service.attachTo === 'PROPERTY') return 'One visit for the property';
  const coverage = coverageNames.get(service.coverageId ?? '') ?? 'Covered equipment';
  if (service.scaling === 'MULTIPLY') return `${coverage} · one visit per unit`;
  return `${coverage} · one extended visit${
    service.extraUnitMinutes > 0 ? ` · +${service.extraUnitMinutes} min / additional unit` : ''
  }`;
}

export function MembershipSalePromiseSummary({
  plan,
  billingOption,
  services,
  servicesLoading = false,
  servicesError = false,
  equipment,
  equipmentState,
  allowLegacyUnitVisitsWithoutEquipment = false,
  historicalVisits = false,
}: MembershipSalePromiseSummaryProps) {
  const coverageRules = plan.coverageRules ?? [];
  const coverageNames = new Map(
    coverageRules.map((rule) => [rule.id, formatEquipmentCategory(rule.equipmentCategory)]),
  );
  const soldHomePricing = billingOption
    ? priceMembershipOptionForEquipment({
        option: billingOption,
        coverageRules,
        equipment,
      })
    : null;
  const occurrenceCount = plannedMembershipVisits({
    plan,
    billingOption,
    services,
    equipment,
    equipmentState,
    allowLegacyUnitVisitsWithoutEquipment,
  });
  const discountScopes = (plan.eligibleLineItemTypes ?? [])
    .map((type) => ITEM_TYPE_LABELS[type.toUpperCase()])
    .filter((value): value is string => Boolean(value));
  const eligibleItemCount = plan.eligibleLineItemIds?.length ?? 0;
  const excludedItemCount = plan.excludedLineItemIds?.length ?? 0;
  const planEligibilitySummary =
    eligibleItemCount > 0
      ? `Limited to ${eligibleItemCount} selected pricebook item${eligibleItemCount === 1 ? '' : 's'}.`
      : discountScopes.length > 0
        ? `Applies to ${formatList(discountScopes)}.`
        : 'Applies to all eligible pricebook items.';
  const exclusionSummary =
    excludedItemCount > 0
      ? ` Excludes ${excludedItemCount} selected item${excludedItemCount === 1 ? '' : 's'}.`
      : '';
  const hasSimpleDiscount = Number(plan.discountPercent) > 0;
  const hasCustomPricing = (plan.discountTiers?.length ?? 0) > 0;
  const waivers = plan.waivers ?? [];

  return (
    <section
      aria-labelledby="membership-sale-promise-title"
      className="overflow-hidden rounded-lg border border-border bg-muted/20"
    >
      <div className="flex flex-wrap items-start justify-between gap-2 border-b border-border px-4 py-3">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            Sale promise
          </p>
          <h3 id="membership-sale-promise-title" className="mt-0.5 text-sm font-semibold">
            What this membership includes
          </h3>
        </div>
        <span className="rounded-full border border-border bg-background px-2 py-1 text-[11px] font-medium text-muted-foreground">
          Frozen at activation
        </span>
      </div>

      <div className="grid divide-y divide-border lg:grid-cols-3 lg:divide-x lg:divide-y-0">
        <div className="space-y-3 p-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
              Equipment coverage
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              The units and extra-unit price sold with this membership.
            </p>
          </div>
          {coverageRules.length > 0 ? (
            <ul className="space-y-2">
              {coverageRules.map((coverage) => {
                const price = extraUnitPriceOf(coverage);
                return (
                  <li key={coverage.id} className="text-sm">
                    <p className="font-medium">
                      {formatEquipmentCategory(coverage.equipmentCategory)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {coverage.includedQuantity} included ·{' '}
                      {price.extraUnitPrice > 0
                        ? `${new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: plan.priceCurrency || 'USD',
                          }).format(price.extraUnitPrice)} ${
                            EXTRA_UNIT_PRICE_UNIT_LABELS[price.extraUnitPricePer]
                          } for each additional unit`
                        : 'additional units included at no extra charge'}
                    </p>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">No equipment-specific coverage.</p>
          )}

          {coverageRules.length > 0 ? (
            <div className="rounded-md border border-border bg-background px-3 py-2 text-xs">
              {equipmentState === 'AWAITING_LOCATION' ? (
                <p className="text-muted-foreground">
                  Select a customer and service location to confirm the covered units.
                </p>
              ) : equipmentState === 'LOADING' ? (
                <p className="text-muted-foreground">Matching this home&apos;s equipment…</p>
              ) : equipmentState === 'ERROR' ? (
                <p className="text-destructive">
                  Covered equipment could not be loaded for this location.
                </p>
              ) : (
                <>
                  <p className="font-medium">
                    This home: {soldHomePricing?.equipmentIds.length ?? 0} matching unit
                    {(soldHomePricing?.equipmentIds.length ?? 0) === 1 ? '' : 's'}
                  </p>
                  <p className="mt-0.5 text-muted-foreground">
                    {(soldHomePricing?.equipmentIds.length ?? 0) === 0
                      ? 'No covered equipment is currently recorded at this location.'
                      : billingOption && (soldHomePricing?.extraPeriodAmount ?? 0) > 0
                        ? `Adds ${new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: plan.priceCurrency || 'USD',
                          }).format(
                            soldHomePricing?.extraPeriodAmount ?? 0,
                          )} ${membershipBillingPeriodLabel(billingOption)}.`
                        : 'No extra-unit charge.'}
                  </p>
                </>
              )}
            </div>
          ) : null}
        </div>

        <div className="space-y-3 p-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
              {historicalVisits ? 'Original included visits' : 'Included visits'}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              {services.length > 0 ? occurrenceLabel(occurrenceCount) : 'No included visits'}
            </p>
            {historicalVisits ? (
              <p className="mt-1 text-xs text-muted-foreground">
                Original visit terms. Current schedules and remaining visits are shown under
                Recurring services.
              </p>
            ) : null}
          </div>
          {servicesLoading ? (
            <p className="text-sm text-muted-foreground">Loading included visits…</p>
          ) : servicesError ? (
            <p className="text-sm text-destructive">Included visits could not be loaded.</p>
          ) : services.length > 0 ? (
            <ul className="space-y-2.5">
              {services.map((service) => (
                <li key={service.id} className="text-sm">
                  <p className="font-medium">{service.label || 'Included service'}</p>
                  <p className="text-xs text-muted-foreground">{scheduleSummary(service)}</p>
                  <p className="text-xs text-muted-foreground">
                    {serviceDelivery(service, coverageNames)}
                  </p>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">This is a pricing-only membership.</p>
          )}
        </div>

        <div className="space-y-3 p-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
              Member savings
            </p>
            <p className="mt-1 text-xs text-muted-foreground">Automatic benefits on future work.</p>
          </div>
          {hasCustomPricing ? (
            <div className="text-sm">
              <p className="font-medium">Custom member pricing</p>
              <p className="text-xs text-muted-foreground">
                {plan.discountTiers?.length} rate{plan.discountTiers?.length === 1 ? '' : 's'} apply
                by item scope.
              </p>
              <p className="text-xs text-muted-foreground">
                {planEligibilitySummary}
                {exclusionSummary}
              </p>
            </div>
          ) : hasSimpleDiscount ? (
            <div className="text-sm">
              <p className="font-medium">{plan.discountPercent}% off</p>
              <p className="text-xs text-muted-foreground">
                {planEligibilitySummary}
                {exclusionSummary}
              </p>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No automatic member discount.</p>
          )}

          {waivers.length > 0 ? (
            <div className="text-sm">
              <p className="font-medium">Waived fees</p>
              <ul className="mt-1 space-y-1 text-xs text-muted-foreground">
                {waivers.map((waiver, index) => (
                  <li key={`${waiver.lineItemId ?? waiver.name}-${index}`}>{waiver.name}</li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
